import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Tag,
  Typography,
  message,
  Popconfirm,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import ROUTE_PATHS from "../routes/routePaths";
import {
  getInstitutions,
  getTransactionScheduleBankOptionsByInstitution,
  getTransactionScheduleManageDetail,
  getTransactionScheduleUnagreedReservations,
  saveTransactionScheduleManageDetail,
} from "../api/transactionScheduleApi";
import {
  getParentStatusColor,
  formatAmount,
} from "./transactionSchedule/transactionScheduleUi";

const { Text } = Typography;

function createEmptyAllocation(defaultPurpose = "", defaultAmount = null) {
  return {
    id: null,
    allocatedAmount: defaultAmount,
    paymentPurpose: defaultPurpose || "",
    originalBankAccountId: undefined,
    manualBankAccountId: undefined,
    manualFilledAt: dayjs(),
    manualFee: 50.0,
    swiftFee: 100.0,
    pdfBankOption: "golomt",
  };
}

function normalizeAllocationsForForm(
  allocations,
  parentPaymentPurpose,
  paymentAmount,
) {
  if (!allocations || allocations.length === 0) {
    return [createEmptyAllocation(parentPaymentPurpose, paymentAmount ?? null)];
  }

  return allocations.map((item) => ({
    id: item.id ?? null,
    allocatedAmount: item.allocatedAmount ?? null,
    paymentPurpose: item.paymentPurpose ?? parentPaymentPurpose ?? "",
    originalBankAccountId: item.originalBankAccountId ?? undefined,
    manualBankAccountId: item.manualBankAccountId ?? undefined,
    manualFilledAt: item.manualFilledAt ? dayjs(item.manualFilledAt) : null,
    manualFee: item.manualFee ?? 50.0,
    swiftFee: item.swiftFee ?? 100.0,
    pdfBankOption: item.pdfBankOption || "golomt",
  }));
}

function normalizeInstitutionName(value = "") {
  return String(value)
    .toUpperCase()
    .replace(/\bLLC\b/g, "ХХК")
    .replace(/\bLTD\b/g, "")
    .replace(/\bINC\b/g, "")
    .replace(/\bCO\b/g, "")
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\bХХК\b/g, "")
    .trim();
}

function findBestInstitutionIdFromPayer(payer, institutions = []) {
  const normalizedPayer = normalizeInstitutionName(payer);

  if (!normalizedPayer) return undefined;

  const exact = institutions.find(
    (item) => normalizeInstitutionName(item?.name) === normalizedPayer,
  );
  if (exact) return exact.id;

  const contains = institutions.find((item) => {
    const normalizedInstitution = normalizeInstitutionName(item?.name);
    return (
      normalizedInstitution &&
      (normalizedInstitution.includes(normalizedPayer) ||
        normalizedPayer.includes(normalizedInstitution))
    );
  });

  return contains?.id;
}

function TransactionScheduleManageModal({
  open,
  onClose,
  record,
  onSaved,
  canAgree = false,
  canUnagree = false,
  onAgreeAllocation,
  onUnagreeAllocation,
  onRunAllocationCheck,
  runningCheckIds = [],
  agreeingAllocationIds = [],
  unagreeingAllocationIds = [],
}) {
  const navigate = useNavigate();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(false);
  const [institutions, setInstitutions] = useState([]);
  const [manageDetail, setManageDetail] = useState(null);
  const [bankOptions, setBankOptions] = useState([]);
  const [reservationData, setReservationData] = useState(null);

  const allocations = Form.useWatch("allocations", form) || [];
  const selectedInstitutionId = Form.useWatch("institutionId", form);


  const loadInitialData = async () => {
    if (!record?.id) return;

    try {
      setLoadingInitial(true);

      const [detail, institutionList] = await Promise.all([
        getTransactionScheduleManageDetail(record.id),
        getInstitutions(),
      ]);

      setManageDetail(detail);
      setInstitutions(institutionList || []);

      const paymentAmount = detail?.paymentAmount ?? null;
      const formAllocations = normalizeAllocationsForForm(
        detail?.allocations || [],
        detail?.parentPaymentPurpose || "",
        paymentAmount,
      );

      const existingInstitutionId =
        detail?.allocations?.[0]?.institutionId ?? undefined;

      const suggestedInstitutionId =
        existingInstitutionId ??
        findBestInstitutionIdFromPayer(detail?.payer, institutionList || []);

      form.setFieldsValue({
        institutionId: suggestedInstitutionId,
        allocations: formAllocations,
      });

      if (suggestedInstitutionId) {
        const [options, reservations] = await Promise.all([
          getTransactionScheduleBankOptionsByInstitution(
            suggestedInstitutionId,
            detail?.currency,
          ),
          getTransactionScheduleUnagreedReservations(
            record.id,
            suggestedInstitutionId,
            detail?.currency,
          ),
        ]);

        setBankOptions(options || []);
        setReservationData(reservations || null);
      } else {
        setBankOptions([]);
        setReservationData(null);
      }
    } catch (error) {
      message.error(error.message || "Удирдах мэдээлэл ачаалж чадсангүй");
    } finally {
      setLoadingInitial(false);
    }
  };


  useEffect(() => {
    if (!open || !record?.id) return;
    loadInitialData();
  }, [open, record?.id]);


  const institutionOptions = useMemo(
    () =>
      (institutions || []).map((item) => ({
        label: item.name,
        value: item.id,
      })),
    [institutions],
  );

  const bankOptionMap = useMemo(() => {
    const map = new Map();
    (bankOptions || []).forEach((item) => map.set(item.id, item));
    return map;
  }, [bankOptions]);


  const allocationMetaMap = useMemo(() => {
    const map = new Map();

    (manageDetail?.allocations || []).forEach((item, index) => {
      const key = item?.id ?? `new-${index}`;
      map.set(key, item);
    });

    return map;
  }, [manageDetail]);

  const getAllocationMeta = (row, index) => {
    const key = row?.id ?? `new-${index}`;
    return allocationMetaMap.get(key) || null;
  };

  const isAllocationAgreed = (row, index) => {
    const meta = getAllocationMeta(row, index);

    return (
      meta?.agreed === true ||
      meta?.isAgreed === true ||
      meta?.status === "AGREED" ||
      meta?.fillType === "AGREED"
    );
  };

  const getAllocationCheckSummary = (row, index) => {
    const meta = getAllocationMeta(row, index);

    if (!meta) return [];

    if (Array.isArray(meta.checkSummaryStatuses)) {
      return meta.checkSummaryStatuses;
    }

    if (Array.isArray(meta.checkStatuses)) {
      return meta.checkStatuses;
    }

    return [];
  };


  const manualReservedMap = useMemo(() => {
    const map = new Map();

    (reservationData?.manualBankAccountUnagreedAmounts || []).forEach(
      (item) => {
        map.set(item.bankAccountId, Number(item.amount || 0));
      },
    );

    return map;
  }, [reservationData]);

  const allocatedTotal = useMemo(() => {
    return (allocations || []).reduce((sum, row) => {
      const value = Number(row?.allocatedAmount || 0);
      return sum + (Number.isFinite(value) ? value : 0);
    }, 0);
  }, [allocations]);

  const paymentAmount = Number(manageDetail?.paymentAmount || 0);
  const remainingAmount = paymentAmount - allocatedTotal;
  const amountMatched = Math.abs(remainingAmount) < 0.000001;

  const totalInstitutionWalletBalance = useMemo(() => {
    return (bankOptions || []).reduce((sum, item) => {
      const value = Number(item?.walletBalance || 0);
      return sum + (Number.isFinite(value) ? value : 0);
    }, 0);
  }, [bankOptions]);

  const institutionReservedUnagreedTotal = Number(
    reservationData?.institutionUnagreedAmountTotal || 0,
  );

  const institutionAvailableToAllocate =
    totalInstitutionWalletBalance - institutionReservedUnagreedTotal;

  const hasInstitutionSelected = !!selectedInstitutionId;
  const institutionWalletEnough =
    !hasInstitutionSelected || institutionAvailableToAllocate >= allocatedTotal;

  const institutionWalletShortAmount = Math.max(
    allocatedTotal - institutionAvailableToAllocate,
    0,
  );

  const allocationValidation = useMemo(() => {
    const issues = [];

    (allocations || []).forEach((row, index) => {
      const rowNo = index + 1;
      const amount = Number(row?.allocatedAmount || 0);
      const purpose = row?.paymentPurpose;
      const originalBankAccountId = row?.originalBankAccountId;
      const manualBankAccountId = row?.manualBankAccountId;
      const manualFilledAt = row?.manualFilledAt;
      const manualFee = row?.manualFee;
      const swiftFee = row?.swiftFee;

      if (!(amount > 0)) {
        issues.push(`Хуваарилалт ${rowNo}: дүн оруулна уу`);
      }

      if (!purpose || !String(purpose).trim()) {
        issues.push(`Хуваарилалт ${rowNo}: гүйлгээний утга оруулна уу`);
      }

      if (!originalBankAccountId) {
        issues.push(`Хуваарилалт ${rowNo}: үндсэн банкны данс сонгоно уу`);
      }

      if (!manualBankAccountId) {
        issues.push(`Хуваарилалт ${rowNo}: гараар сонгосон данс сонгоно уу`);
      }

      if (!manualFilledAt) {
        issues.push(`Хуваарилалт ${rowNo}: гараар бөглөсөн огноо оруулна уу`);
      }

      if (manualFee === undefined || manualFee === null || manualFee === "") {
        issues.push(`Хуваарилалт ${rowNo}: гараар нэмсэн шимтгэл оруулна уу`);
      }

      if (swiftFee === undefined || swiftFee === null || swiftFee === "") {
        issues.push(`Хуваарилалт ${rowNo}: SWIFT шимтгэл оруулна уу`);
      }
    });

    return {
      valid: issues.length === 0,
      issues,
    };
  }, [allocations]);

  const manualWalletIssues = useMemo(() => {
    const issues = [];

    (allocations || []).forEach((row, index) => {
      const rowNo = index + 1;
      const amount = Number(row?.allocatedAmount || 0);

      if (!(amount > 0) || !row?.manualBankAccountId) return;

      const manualBank = bankOptionMap.get(row.manualBankAccountId);
      const manualWallet = Number(manualBank?.walletBalance || 0);
      const manualReserved = Number(
        manualReservedMap.get(row.manualBankAccountId) || 0,
      );
      const manualAvailable = manualWallet - manualReserved;

      if (manualAvailable < amount) {
        issues.push(
          `Хуваарилалт ${rowNo}: гараар сонгосон дансны боломжит хийсвэр үлдэгдэл хүрэлцэхгүй байна (${formatAmount(
            manualAvailable,
          )} < ${formatAmount(amount)})`,
        );
      }
    });

    return {
      valid: issues.length === 0,
      issues,
    };
  }, [allocations, bankOptionMap, manualReservedMap]);

  const hasAgreedAllocation = useMemo(() => {
    return allocations.some((row, index) => isAllocationAgreed(row, index));
  }, [allocations]);

  const getLowWalletOptionIdsForRow = (rowAmount) => {
    const amount = Number(rowAmount || 0);
    if (!(amount > 0)) return new Set();

    return new Set(
      (bankOptions || [])
        .filter((item) => {
          const wallet = Number(item?.walletBalance || 0);
          const reserved = Number(manualReservedMap.get(item.id) || 0);
          const available = wallet - reserved;
          return available < amount;
        })
        .map((item) => item.id),
    );
  };

  const bankSelectOptions = useMemo(() => {
    return (bankOptions || []).map((item) => {
      const bankName = item.bank?.displayName || item.bank || "-";
      const currency = item.currencyCode || item.currency || "-";
      const accountNumber = item.accountNumber || "-";
      const balance = Number(item.balance || 0);
      const walletBalance = Number(item.walletBalance || 0);
      const reservedUnagreedAmount = Number(
        manualReservedMap.get(item.id) || 0,
      );
      const availableWallet = walletBalance - reservedUnagreedAmount;

      return {
        label:
          `${bankName} | ${currency} | ${accountNumber}` +
          ` | Үндсэн үлдэгдэл: ${formatAmount(balance)}` +
          ` | Хийсвэр үлдэгдэл: ${formatAmount(walletBalance)}` +
          ` | Зөвшөөрөөгүй хүлээгдэж буй: ${formatAmount(reservedUnagreedAmount)}` +
          ` | Боломжит: ${formatAmount(availableWallet)}`,
        value: item.id,
      };
    });
  }, [bankOptions, manualReservedMap]);

  const formReady =
    hasInstitutionSelected &&
    allocations.length > 0 &&
    allocationValidation.valid &&
    amountMatched;

  const canSave =
    formReady &&
    institutionWalletEnough &&
    manualWalletIssues.valid &&
    !loading &&
    !loadingInitial;

  const walletRoute = selectedInstitutionId
    ? ROUTE_PATHS.INSTITUTION_WALLETS.replace(
      ":institutionId",
      selectedInstitutionId,
    )
    : null;

  const handleInstitutionChange = async (institutionId) => {
    form.setFieldsValue({
      allocations: (form.getFieldValue("allocations") || []).map((row) => ({
        ...row,
        originalBankAccountId: undefined,
        manualBankAccountId: undefined,
      })),
    });

    if (!institutionId) {
      setBankOptions([]);
      setReservationData(null);
      return;
    }

    try {
      const [options, reservations] = await Promise.all([
        getTransactionScheduleBankOptionsByInstitution(
          institutionId,
          manageDetail?.currency,
        ),
        getTransactionScheduleUnagreedReservations(
          record.id,
          institutionId,
          manageDetail?.currency,
        ),
      ]);

      setBankOptions(options || []);
      setReservationData(reservations || null);
    } catch (error) {
      message.error(error.message || "Банкны сонголтууд ачаалж чадсангүй");
    }
  };

  const handleAddAllocation = () => {
    const current = form.getFieldValue("allocations") || [];
    const isFirst = current.length === 0;

    const defaultAmount = isFirst
      ? (manageDetail?.paymentAmount ?? null)
      : Math.max(remainingAmount, 0);

    form.setFieldsValue({
      allocations: [
        ...current,
        createEmptyAllocation(
          manageDetail?.parentPaymentPurpose || "",
          defaultAmount,
        ),
      ],
    });
  };

  const handleRemoveAllocation = (index) => {
    const current = [...(form.getFieldValue("allocations") || [])];
    current.splice(index, 1);

    if (current.length === 0) {
      form.setFieldsValue({
        allocations: [
          createEmptyAllocation(
            manageDetail?.parentPaymentPurpose || "",
            manageDetail?.paymentAmount ?? null,
          ),
        ],
      });
      return;
    }

    form.setFieldsValue({
      allocations: current,
    });
  };

  const handleOpenWallets = () => {
    if (!walletRoute) return;
    onClose?.();
    navigate(walletRoute);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      const payload = {
        institutionId: values.institutionId,
        allocations: (values.allocations || []).map((row) => ({
          id: row.id || null,
          allocatedAmount: row.allocatedAmount,
          paymentPurpose: row.paymentPurpose,
          originalBankAccountId: row.originalBankAccountId || null,
          manualBankAccountId: row.manualBankAccountId || null,
          manualFilledAt: row.manualFilledAt
            ? row.manualFilledAt.format("YYYY-MM-DDTHH:mm:ss")
            : null,
          manualFee: row.manualFee ?? null,
          swiftFee: row.swiftFee ?? null,
          pdfBankOption: row.pdfBankOption || "golomt",
        })),
      };

      if (!payload.institutionId) {
        message.error("Байгууллага сонгоно уу");
        return;
      }

      if (!payload.allocations.length) {
        message.error("Дор хаяж нэг хуваарилалт шаардлагатай");
        return;
      }

      if (!allocationValidation.valid) {
        message.error(
          allocationValidation.issues[0] ||
          "Бүх шаардлагатай талбарыг бөглөнө үү",
        );
        return;
      }

      if (!amountMatched) {
        message.error("Хуваарилсан нийт дүн төлбөрийн дүнтэй тэнцэх ёстой");
        return;
      }

      if (!institutionWalletEnough) {
        message.error(
          "Байгууллагын боломжит хийсвэр үлдэгдэл хүрэлцэхгүй байна",
        );
        return;
      }

      if (!manualWalletIssues.valid) {
        message.error(
          manualWalletIssues.issues[0] ||
          "Гараар сонгосон дансны боломжит хийсвэр үлдэгдэл хүрэлцэхгүй байна",
        );
        return;
      }

      setLoading(true);
      await saveTransactionScheduleManageDetail(record.id, payload);
      message.success("Гүйлгээний хуваарь хадгалагдлаа");
      await onSaved?.();
      await loadInitialData();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.message || "Гүйлгээний хуваарь хадгалж чадсангүй");
    } finally {
      setLoading(false);
    }
  };

  const showInstitutionWalletAlert =
    !institutionWalletEnough && selectedInstitutionId;
  const showManualWalletAlert = manualWalletIssues.issues.length > 0;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      confirmLoading={loading}
      okButtonProps={{ disabled: !canSave || hasAgreedAllocation }}
      width={1400}
      title={`Гүйлгээний хуваарь удирдах${record?.id ? ` #${record.id}` : ""}`}
      okText="Хадгалах"
      cancelText="Болих"
      destroyOnClose
    >
      <div className="space-y-4">
        <Card loading={loadingInitial}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Text strong>Төлөгч:</Text>
              <div>{manageDetail?.payer || "-"}</div>
            </Col>
            <Col xs={24} md={8}>
              <Text strong>Төлбөрийн дүн:</Text>
              <div>{formatAmount(manageDetail?.paymentAmount)}</div>
            </Col>
            <Col xs={24} md={8}>
              <Text strong>Валют:</Text>
              <div>{manageDetail?.currency || "-"}</div>
            </Col>

            <Col xs={24} md={8}>
              <Text strong>Хүлээн авагч байгууллага:</Text>
              <div>{manageDetail?.beneficiaryCompany || "-"}</div>
            </Col>
            <Col xs={24} md={8}>
              <Text strong>Хүлээн авагч банк:</Text>
              <div>{manageDetail?.beneficiaryBank || "-"}</div>
            </Col>
            <Col xs={24} md={8}>
              <Text strong>Хүлээн авагчийн данс:</Text>
              <div>{manageDetail?.beneficiaryAccount || "-"}</div>
            </Col>

            <Col xs={24}>
              <Text strong>Ерөнхий гүйлгээний утга:</Text>
              <div>{manageDetail?.parentPaymentPurpose || "-"}</div>
            </Col>

            <Col xs={24} md={6}>
              <Text strong>Нийт хуваарилсан дүн:</Text>
              <div>{formatAmount(allocatedTotal)}</div>
            </Col>
            <Col xs={24} md={6}>
              <Text strong>Үлдэгдэл:</Text>
              <div
                style={{
                  color: amountMatched ? "#389e0d" : "#cf1322",
                  fontWeight: 600,
                }}
              >
                {formatAmount(remainingAmount)}
              </div>
            </Col>
            <Col xs={24} md={6}>
              <Text strong>Хуваарилалтын тоо:</Text>
              <div>{allocations.length}</div>
            </Col>
            <Col xs={24} md={6}>
              <Text strong>Ерөнхий төлөв:</Text>
              <div>
                <Tag
                  color={getParentStatusColor(
                    manageDetail?.parentDisplayStatus,
                  )}
                >
                  {manageDetail?.parentDisplayStatus === "SINGLE"
                    ? "Нэг"
                    : manageDetail?.parentDisplayStatus === "PARTIAL"
                      ? "Хэсэгчилсэн"
                      : manageDetail?.parentDisplayStatus === "EMPTY"
                        ? "Хоосон"
                        : "-"}
                </Tag>
              </div>
            </Col>
          </Row>
        </Card>

        {!hasAgreedAllocation && (
          <>
            {selectedInstitutionId ? (
              <Alert
                type="info"
                showIcon
                message="Хийсвэр үлдэгдэл шалгалтын тооцоо"
                description={
                  <div className="flex flex-col gap-1">
                    <div>
                      Байгууллагын нийт хийсвэр үлдэгдэл:{" "}
                      <strong>
                        {formatAmount(totalInstitutionWalletBalance)}{" "}
                        {manageDetail?.currency || ""}
                      </strong>
                    </div>
                    <div>
                      Бусад зөвшөөрөөгүй хуваарилалтын нийлбэр:{" "}
                      <strong>
                        {formatAmount(institutionReservedUnagreedTotal)}{" "}
                        {manageDetail?.currency || ""}
                      </strong>
                    </div>
                    <div>
                      Одоо ашиглах боломжит дүн:{" "}
                      <strong>
                        {formatAmount(institutionAvailableToAllocate)}{" "}
                        {manageDetail?.currency || ""}
                      </strong>
                    </div>
                    <div>
                      Одоо хадгалах гэж буй дүн:{" "}
                      <strong>
                        {formatAmount(allocatedTotal)}{" "}
                        {manageDetail?.currency || ""}
                      </strong>
                    </div>
                  </div>
                }
              />
            ) : null}

            {showInstitutionWalletAlert ? (
              <Alert
                type="warning"
                showIcon
                message="Нийт боломжит хийсвэр үлдэгдэл хүрэлцэхгүй байна"
                description={
                  <div className="flex flex-col gap-2">
                    <div>
                      Нийт хийсвэр үлдэгдэл:{" "}
                      <strong>
                        {formatAmount(totalInstitutionWalletBalance)}{" "}
                        {manageDetail?.currency || ""}
                      </strong>
                    </div>
                    <div>
                      Бусад зөвшөөрөөгүй хуваарилалт:{" "}
                      <strong>
                        {formatAmount(institutionReservedUnagreedTotal)}{" "}
                        {manageDetail?.currency || ""}
                      </strong>
                    </div>
                    <div>
                      Боломжит дүн:{" "}
                      <strong>
                        {formatAmount(institutionAvailableToAllocate)}{" "}
                        {manageDetail?.currency || ""}
                      </strong>
                    </div>
                    <div>
                      Хадгалах гэж буй дүн:{" "}
                      <strong>
                        {formatAmount(allocatedTotal)}{" "}
                        {manageDetail?.currency || ""}
                      </strong>
                    </div>
                    <div>
                      Дутуу дүн:{" "}
                      <strong>
                        {formatAmount(institutionWalletShortAmount)}{" "}
                        {manageDetail?.currency || ""}
                      </strong>
                    </div>
                    <div>Эхлээд мөнгө байршуулсны дараа хадгална уу.</div>
                    <div>
                      <Button
                        type="link"
                        style={{ padding: 0 }}
                        onClick={handleOpenWallets}
                      >
                        Байгууллагын хийсвэр үлдэгдэл рүү очих
                      </Button>
                    </div>
                  </div>
                }
              />
            ) : null}

            {showManualWalletAlert ? (
              <Alert
                type="error"
                showIcon
                message="Гараар сонгосон дансны боломжит хийсвэр үлдэгдэл хүрэлцэхгүй байна"
                description={
                  <div className="flex flex-col gap-1">
                    {manualWalletIssues.issues.map((issue, index) => (
                      <div key={index}>{issue}</div>
                    ))}
                  </div>
                }
              />
            ) : null}

            {!allocationValidation.valid && allocations.length > 0 ? (
              <Alert
                type="info"
                showIcon
                message="Бүх шаардлагатай талбарыг бөглөнө үү"
                description={
                  <div className="flex flex-col gap-1">
                    {allocationValidation.issues.map((issue, index) => (
                      <div key={index}>{issue}</div>
                    ))}
                  </div>
                }
              />
            ) : null}
          </>
        )}

        {hasAgreedAllocation ? (
          <Alert
            type="warning"
            showIcon
            message="Зөвшөөрөгдсөн хуваарилалт байна"
            description="Зөвшөөрөгдсөн мөр байгаа үед талбарууд болон хадгалах товч идэвхгүй байна. Өөрчлөх бол тухайн мөрийн зөвшөөрлийг эхлээд цуцална уу."
          />
        ) : null}

        <Form form={form} layout="vertical">
          <Card style={{ marginBottom: 16 }}>
            <Row gutter={[16, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="institutionId"
                  label="Байгууллага"
                  rules={[
                    { required: true, message: "Байгууллага заавал сонгоно" },
                  ]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    options={institutionOptions}
                    onChange={handleInstitutionChange}
                    disabled={hasAgreedAllocation}
                  />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Text strong>Банкны сонголтууд</Text>
                <Card size="small" style={{ marginTop: 8 }}>
                  <Space direction="vertical" style={{ width: "100%" }}>
                    {bankOptions.length ? (
                      bankOptions.map((opt) => {
                        const bankName =
                          opt.bank?.displayName || opt.bank || "-";
                        const currency =
                          opt.currencyCode || opt.currency || "-";
                        const accountNumber = opt.accountNumber || "-";
                        const balance = Number(opt.balance || 0);
                        const walletBalance = Number(opt.walletBalance || 0);
                        const reserved = Number(
                          manualReservedMap.get(opt.id) || 0,
                        );
                        const available = walletBalance - reserved;

                        return (
                          <div key={opt.id}>
                            {`${bankName} | ${currency} | ${accountNumber} | Үндсэн үлдэгдэл: ${formatAmount(
                              balance,
                            )} | Хийсвэр үлдэгдлийн үлдэгдэл: ${formatAmount(
                              walletBalance,
                            )} | Зөвшөөрөөгүй хүлээгдэж буй: ${formatAmount(
                              reserved,
                            )} | Боломжит: ${formatAmount(available)}`}
                          </div>
                        );
                      })
                    ) : (
                      <Text type="secondary">
                        Банкны сонголт ачаалагдаагүй байна
                      </Text>
                    )}
                  </Space>
                </Card>
              </Col>

            </Row>
          </Card>

          <Form.List name="allocations">
            {(fields) => (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 12,
                  }}
                >
                  <Text strong>Хуваарилалтууд</Text>
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={handleAddAllocation}
                    disabled={hasAgreedAllocation}
                  >
                    Хуваарилалт нэмэх
                  </Button>
                </div>

                {fields.map((field, index) => {
                  const rowEnabled = !!selectedInstitutionId;
                  const rowAmount = Number(
                    allocations?.[index]?.allocatedAmount || 0,
                  );
                  const lowWalletOptionIds =
                    getLowWalletOptionIdsForRow(rowAmount);


                  const rowData = allocations?.[index] || {};
                  const allocationId = rowData?.id;
                  const agreed = isAllocationAgreed(rowData, index);

                  const agreePending = allocationId
                    ? agreeingAllocationIds.includes(allocationId)
                    : false;

                  const unagreePending = allocationId
                    ? unagreeingAllocationIds.includes(allocationId)
                    : false;

                  const checkStatuses = getAllocationCheckSummary(rowData, index);
                  const existingAllocation = !!allocationId;
                  const rowLocked = agreed;

                  return (
                    <Card
                      key={field.key}
                      size="small"
                      title={
                        <Space wrap size={8}>
                          <span>{`Хуваарилалт ${index + 1}`}</span>

                          {existingAllocation ? (
                            agreed ? (
                              <Tag color="green">Зөвшөөрөгдсөн</Tag>
                            ) : (
                              <Tag color="orange">Зөвшөөрөл дутуу</Tag>
                            )
                          ) : (
                            <Tag>Шинэ</Tag>
                          )}

                          {checkStatuses.slice(0, 2).map((status) => (
                            <Tag key={status} color="blue">
                              {status}
                            </Tag>
                          ))}
                          {checkStatuses.length > 2 ? <Tag>+{checkStatuses.length - 2}</Tag> : null}
                        </Space>
                      }
                      extra={
                        <Space wrap size={8}>
                          {existingAllocation && canAgree ? (
                            <Button
                              size="small"
                              type="primary"
                              icon={<CheckOutlined />}
                              loading={agreePending}
                              disabled={agreed || agreePending || unagreePending}
                              onClick={async () => {
                                if (agreePending) return;

                                const ok = await onAgreeAllocation?.(allocationId);
                                if (ok) {
                                  await onSaved?.();
                                }
                              }}
                            >
                              Зөвшөөрөх
                            </Button>
                          ) : null}

                          {existingAllocation && canUnagree ? (
                            <Popconfirm
                              title="Энэ хуваарилалтын зөвшөөрлийг цуцлах уу?"
                              okText="Тийм"
                              cancelText="Үгүй"
                              disabled={!agreed || agreePending || unagreePending}
                              onConfirm={async () => {
                                if (unagreePending || agreePending || !agreed) return;

                                const ok = await onUnagreeAllocation?.(allocationId);
                                if (ok) {
                                  await onSaved?.();
                                  await loadInitialData();
                                }
                              }}
                            >
                              <Button
                                size="small"
                                danger
                                icon={<CloseOutlined />}
                                loading={unagreePending}
                                disabled={!agreed || agreePending || unagreePending}
                              >
                                Цуцлах
                              </Button>
                            </Popconfirm>
                          ) : null}

                          {existingAllocation && agreed ? (
                            <Button
                              size="small"
                              icon={<SyncOutlined />}
                              loading={runningCheckIds.includes(allocationId)}
                              onClick={() => onRunAllocationCheck?.(allocationId)}
                            >
                              Шалгах
                            </Button>
                          ) : null}

                          {fields.length > 1 ? (
                            <Button
                              danger
                              type="text"
                              icon={<MinusCircleOutlined />}
                              disabled={rowLocked}
                              onClick={() => handleRemoveAllocation(index)}
                            />
                          ) : null}
                        </Space>
                      }
                      style={{
                        marginBottom: 16,
                        background: agreed ? "#f6ffed" : undefined,
                        borderColor: agreed ? "#b7eb8f" : undefined,
                      }}
                    >
                      {!existingAllocation ? (
                        <Alert
                          type="info"
                          showIcon
                          style={{ marginBottom: 12 }}
                          message="Шинэ хуваарилалт"
                          description="Энэ мөр одоогоор хадгалагдаагүй байна. Эхлээд хадгалсны дараа нэг бүрчлэн зөвшөөрөх, цуцлах, шалгах үйлдэл идэвхжинэ."
                        />
                      ) : null}
                      {agreed ? (
                        <Alert
                          type="success"
                          showIcon
                          style={{ marginBottom: 12 }}
                          message="Энэ хуваарилалт зөвшөөрөгдсөн байна"
                          description="Талбарууд түгжигдсэн. Өөрчлөх бол эхлээд зөвшөөрлийг цуцална уу."
                        />
                      ) : null}
                      <Row gutter={[16, 0]}>
                        <Col span={0} style={{ display: "none" }}>
                          <Form.Item name={[field.name, "id"]}>
                            <Input />
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={8}>
                          <Form.Item
                            name={[field.name, "allocatedAmount"]}
                            label="Хуваарилсан дүн"
                            rules={[
                              {
                                required: true,
                                message: "Хуваарилсан дүн заавал оруулна",
                              },
                            ]}
                          >
                            <InputNumber
                              style={{ width: "100%" }}
                              min={0}
                              step={0.01}
                              precision={2}
                              disabled={rowLocked}
                            />
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={16}>
                          <Form.Item
                            name={[field.name, "paymentPurpose"]}
                            label="Гүйлгээний утга"
                            rules={[
                              {
                                required: true,
                                message: "Гүйлгээний утга заавал оруулна",
                              },
                            ]}
                          >
                            <Input disabled={rowLocked} />
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                          <Form.Item
                            name={[field.name, "originalBankAccountId"]}
                            label="Үндсэн банкны данс"
                            rules={[
                              {
                                required: true,
                                message: "Үндсэн банкны данс сонгоно уу",
                              },
                            ]}
                          >
                            <Select
                              allowClear
                              showSearch
                              optionFilterProp="label"
                              disabled={!rowEnabled || rowLocked}
                              options={bankSelectOptions}
                            />
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                          <Form.Item
                            name={[field.name, "manualBankAccountId"]}
                            label="Гараар сонгосон данс"
                            rules={[
                              {
                                required: true,
                                message: "Гараар сонгосон данс сонгоно уу",
                              },
                            ]}
                          >
                            <Select
                              allowClear
                              showSearch
                              optionFilterProp="label"
                              disabled={!rowEnabled || rowLocked}
                              optionRender={(option) => {
                                const isLow = lowWalletOptionIds.has(
                                  option.value,
                                );
                                return (
                                  <div
                                    style={{
                                      color: isLow ? "#cf1322" : undefined,
                                    }}
                                  >
                                    {option.label}
                                    {isLow
                                      ? " | Боломжит үлдэгдэл хүрэлцэхгүй"
                                      : ""}
                                  </div>
                                );
                              }}
                              options={bankSelectOptions}
                            />
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={8}>
                          <Form.Item
                            name={[field.name, "manualFilledAt"]}
                            label="Гараар бөглөсөн огноо"
                            rules={[
                              {
                                required: true,
                                message: "Гараар бөглөсөн огноо оруулна уу",
                              },
                            ]}
                          >
                            <DatePicker
                              showTime
                              style={{ width: "100%" }}
                              disabled={!rowEnabled || rowLocked}
                              format="YYYY-MM-DD HH:mm:ss"
                            />
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={8}>
                          <Form.Item
                            name={[field.name, "manualFee"]}
                            label="Гараар нэмсэн шимтгэл"
                            rules={[
                              {
                                required: true,
                                message: "Гараар нэмсэн шимтгэл оруулна уу",
                              },
                            ]}
                          >
                            <InputNumber
                              style={{ width: "100%" }}
                              min={0}
                              step={0.01}
                              precision={2}
                              disabled={!rowEnabled || rowLocked}
                            />
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={8}>
                          <Form.Item
                            name={[field.name, "swiftFee"]}
                            label="SWIFT шимтгэл"
                            rules={[
                              {
                                required: true,
                                message: "SWIFT шимтгэл оруулна уу",
                              },
                            ]}
                          >
                            <InputNumber
                              style={{ width: "100%" }}
                              min={0}
                              step={0.01}
                              precision={2}
                              disabled={!rowEnabled || rowLocked}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item
                            label="PDF банк"
                            name={[field.name, "pdfBankOption"]}
                            rules={[{ required: true, message: "PDF банк сонгоно уу" }]}
                          >
                            <Select
                              size="small"
                              disabled={rowLocked}
                              options={[
                                { value: "golomt", label: "Голомт" },
                                { value: "khas", label: "ХАС" },
                              ]}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  );
                })}
              </>
            )}
          </Form.List>
        </Form>

        <Divider />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Text strong>
            Нийт шаардлагатай дүн:{" "}
            {formatAmount(manageDetail?.paymentAmount ?? 0)}{" "}
            {manageDetail?.currency || ""}
          </Text>
          <Text strong style={{ color: canSave ? "#389e0d" : "#cf1322" }}>
            Үлдэгдэл: {formatAmount(remainingAmount)}
          </Text>
        </div>
      </div>
    </Modal>
  );
}

export default TransactionScheduleManageModal;
